import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminUsers, AdminUser, AdminUsersParams, AdminUsersResponse } from '../api/admin.api';
import { useDebouncedValue } from './useDebouncedValue';

const DEBOUNCE_MS = 400;
const DEFAULT_LIMIT = 20;

export type AdminUsersSortBy = AdminUsersParams['sortBy'];
export type AdminUsersSortOrder = AdminUsersParams['sortOrder'];

export function useAdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const source = searchParams.get('source') ?? '';
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const sortBy = (searchParams.get('sortBy') as AdminUsersSortBy) ?? 'created_at';
  const sortOrder = (searchParams.get('sortOrder') as AdminUsersSortOrder) ?? 'desc';
  const search = searchParams.get('search') ?? '';

  const [fromInput, setFromInput] = useState(from);
  const [toInput, setToInput] = useState(to);
  const [sourceInput, setSourceInput] = useState(source);
  const [searchInput, setSearchInput] = useState(search);

  const debouncedFrom = useDebouncedValue(fromInput, DEBOUNCE_MS);
  const debouncedTo = useDebouncedValue(toInput, DEBOUNCE_MS);
  const debouncedSource = useDebouncedValue(sourceInput, DEBOUNCE_MS);
  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState<AdminUsersResponse['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateUrl = useCallback(
    (updates: Record<string, string | number>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          const s = String(v).trim();
          if (s === '' || (k === 'page' && v === 1)) {
            if (k === 'page' && v === 1) next.delete('page');
            else if (s === '') next.delete(k);
          } else next.set(k, s);
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const fetchUsers = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: AdminUsersParams = {
      from: debouncedFrom || undefined,
      to: debouncedTo || undefined,
      source: debouncedSource.trim() || undefined,
      page,
      limit: DEFAULT_LIMIT,
      sortBy,
      sortOrder,
      search: debouncedSearch.trim() || undefined,
    };
    getAdminUsers(params)
      .then((res) => {
        setUsers(res.data);
        setPagination(res.pagination ?? null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
        setUsers([]);
        setPagination(null);
      })
      .finally(() => setLoading(false));
  }, [debouncedFrom, debouncedTo, debouncedSource, debouncedSearch, page, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setFromInput(from);
    setToInput(to);
    setSourceInput(source);
    setSearchInput(search);
  }, [from, to, source, search]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debouncedFrom) next.set('from', debouncedFrom);
      else next.delete('from');
      if (debouncedTo) next.set('to', debouncedTo);
      else next.delete('to');
      if (debouncedSource.trim()) next.set('source', debouncedSource.trim());
      else next.delete('source');
      if (debouncedSearch.trim()) next.set('search', debouncedSearch.trim());
      else next.delete('search');
      next.delete('page');
      if (next.toString() === prev.toString()) return prev;
      return next;
    });
  }, [debouncedFrom, debouncedTo, debouncedSource, debouncedSearch, setSearchParams]);

  const setPage = useCallback(
    (p: number) => updateUrl({ page: p }),
    [updateUrl]
  );
  const setSort = useCallback(
    (by: AdminUsersSortBy, order: AdminUsersSortOrder) => {
      updateUrl({ sortBy: by ?? 'created_at', sortOrder: order ?? 'desc', page: 1 });
    },
    [updateUrl]
  );
  const resetFilters = useCallback(() => {
    setFromInput('');
    setToInput('');
    setSourceInput('');
    setSearchInput('');
    setSearchParams({});
  }, [setSearchParams]);

  return {
    users,
    pagination,
    loading,
    error,
    from: fromInput,
    to: toInput,
    source: sourceInput,
    search: searchInput,
    page,
    sortBy,
    sortOrder,
    setFrom: setFromInput,
    setTo: setToInput,
    setSource: setSourceInput,
    setSearch: setSearchInput,
    setPage,
    setSort,
    resetFilters,
    refetch: fetchUsers,
  };
}

